import { useEffect, useCallback } from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import type {
  KinnserClinicDataResponse,
  KinnserPersonInfo,
} from '../../services/api/types.gen';

interface UseKinnserClinicDataSharedOptions {
  agencySystemName: string | undefined;
  kinnserClinicId: string | null | undefined;
}

interface UseKinnserClinicDataSharedReturn {
  clinicData: KinnserClinicDataResponse | null;
  loadingClinicData: boolean;
  clinics: KinnserPersonInfo[];
  loadingClinics: boolean;
  refetchClinicData: () => Promise<KinnserClinicDataResponse | null>;
}

/**
 * Shared hook for fetching and caching Kinnser clinic data.
 * Uses global Zustand store to cache data and prevent duplicate API calls
 * when navigating between SchedulingStep and CreateEpisodeStep.
 */
export function useKinnserClinicDataShared({
  agencySystemName,
  kinnserClinicId,
}: UseKinnserClinicDataSharedOptions): UseKinnserClinicDataSharedReturn {
  const {
    kinnserClinics,
    kinnserClinicsLoading,
    fetchKinnserClinics,
    fetchKinnserClinicData,
    getKinnserClinicData,
    isKinnserClinicDataLoading,

    // used for triggering re-renders after loading state changes
    // @ts-expect-error: intentionally unused - subscribed to trigger re-renders
    kinnserClinicDataLoading, // eslint-disable-line @typescript-eslint/no-unused-vars
  } = useStore(
    useShallow(state => ({
      kinnserClinics: state.kinnserClinics,
      kinnserClinicsLoading: state.kinnserClinicsLoading,
      fetchKinnserClinics: state.fetchKinnserClinics,
      fetchKinnserClinicData: state.fetchKinnserClinicData,
      getKinnserClinicData: state.getKinnserClinicData,
      isKinnserClinicDataLoading: state.isKinnserClinicDataLoading,
      kinnserClinicDataLoading: state.kinnserClinicDataLoading,
    }))
  );

  // Fetch clinics list on mount (if not already cached)
  useEffect(() => {
    if (
      agencySystemName &&
      kinnserClinics.length === 0 &&
      !kinnserClinicsLoading
    ) {
      fetchKinnserClinics(agencySystemName);
    }
  }, [
    agencySystemName,
    kinnserClinics.length,
    kinnserClinicsLoading,
    fetchKinnserClinics,
  ]);

  // Fetch clinic data when clinic ID changes
  useEffect(() => {
    if (agencySystemName && kinnserClinicId) {
      fetchKinnserClinicData(agencySystemName, kinnserClinicId);
    }
  }, [agencySystemName, kinnserClinicId, fetchKinnserClinicData]);

  // Get current clinic data from cache
  const clinicData = kinnserClinicId
    ? getKinnserClinicData(kinnserClinicId)
    : null;
  const loadingClinicData = kinnserClinicId
    ? isKinnserClinicDataLoading(kinnserClinicId)
    : false;

  // Manual refetch function
  const refetchClinicData = useCallback(async () => {
    if (!agencySystemName || !kinnserClinicId) return null;
    return fetchKinnserClinicData(agencySystemName, kinnserClinicId);
  }, [agencySystemName, kinnserClinicId, fetchKinnserClinicData]);

  return {
    clinicData,
    loadingClinicData,
    clinics: kinnserClinics,
    loadingClinics: kinnserClinicsLoading,
    refetchClinicData,
  };
}
